"use no memo";

import { PaymentMethods } from "@repo/database";
import { PaymentMethodSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { PaymentMethod } from "~/providers/payment-method-provider";
import { usePaymentMethodManager } from "~/managers/use-payment-method-manager";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";

export default function PaymentMethodForm({
  paymentMethod,
  onClose,
}: {
  paymentMethod?: PaymentMethod;
  onClose: (force?: boolean) => void;
}) {
  const { t, i18n } = useTranslation("common");
  const { updatePaymentMethod, createPaymentMethod } =
    usePaymentMethodManager();
  const form = useForm({
    schema: PaymentMethodSchema(i18n),
    defaultValues: {
      name: paymentMethod?.name ?? "",
      type: paymentMethod?.type ?? undefined,
      cash_location_id: paymentMethod?.cashLocationId ?? "",
      position: paymentMethod?.position ?? 1,
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (paymentMethod) {
      const { data, error } = await updatePaymentMethod({
        id: paymentMethod.id,
        ...formData,
      });
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError("root", { message: error.message });
      }
    } else {
      const { data, error } = await createPaymentMethod(formData);
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });
  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="payment-method-form"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("PaymentMethod.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("PaymentMethod.name")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("PaymentMethod.position")}</FormLabel>
              <FormControl>
                <ModalInput {...field} type="number" min={1} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("PaymentMethod.type")}</FormLabel>
              <FormControl>
                <MultiSelect
                  disabled={paymentMethod?.isDefault === true}
                  maxSelect={1}
                  placeholder="Select a type..."
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                    void form.trigger();
                  }}
                  options={Object.values(PaymentMethods)
                    .sort()
                    .map((lang) => ({
                      label: lang,
                      value: lang,
                    }))}
                  defaultValue={[field.value]}
                  value={[field.value]}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
